import { useState, useEffect } from "react";
import { apiGet } from "../api";
import { StatCard, PageHeader, Card, Pill, Btn, THead, TD } from "./KioskUI";
import { fmtTime } from "../utils/formatters";

function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}

const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

export default function Overview({ token, go }) {
  const [products, setProducts]     = useState([]);
  const [lowStock, setLowStock]     = useState([]);
  const [todaySales, setTodaySales] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet("/api/inventory/products", token).then(r => r.json()),
      apiGet("/api/inventory/products/low-stock", token).then(r => r.json()),
      apiGet("/api/inventory/sales/today", token).then(r => r.json()),
    ])
      .then(([prods, low, sales]) => {
        setProducts(Array.isArray(prods) ? prods : []);
        setLowStock(Array.isArray(low) ? low : []);
        setTodaySales(Array.isArray(sales) ? sales : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const rev = todaySales.reduce((s, r) => s + Number(r.total_price || 0), 0);
  const catCount = new Set(products.map(p => p.category_name).filter(Boolean)).size;

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
        <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <PageHeader title="Dashboard" mm="အကျဉ်းချုပ်" desc={`${today} · Store open`}>
        <Btn kind="ghost" icon="download" sm>Export</Btn>
        <Btn kind="acc" icon="plus" onClick={() => go("sale")}>New Sale</Btn>
      </PageHeader>

      {/* 4 stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 18 }}>
        <StatCard
          label="Total Products"
          value={products.length}
          icon="box"
          tone="blue"
          sub={`across ${catCount} categor${catCount === 1 ? "y" : "ies"}`}
        />
        <StatCard
          label="Low / Out of Stock"
          value={lowStock.length}
          icon="alert"
          tone="amber"
          sub="needs restock"
        />
        <StatCard
          label="Sales Today"
          value={todaySales.length}
          icon="sale"
          tone="green"
          sub={`${todaySales.length} invoice${todaySales.length !== 1 ? "s" : ""}`}
        />
        <StatCard
          label="Revenue Today"
          value={(rev / 1e6).toFixed(2) + "M"}
          unit="MMK"
          icon="payments"
          tone="blue"
          sub={MMK(rev) + " Ks"}
        />
      </div>

      {/* Two-column: sales table | low stock */}
      <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 16 }}>

        {/* Today's Sales */}
        <Card>
          <div style={{
            padding: "16px 20px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: "1px solid var(--hair)",
          }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>
              Today's Sales
            </div>
            <button
              onClick={() => go("payments")}
              style={{ border: "none", background: "transparent", color: "var(--primary)", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}
            >
              View all
            </button>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={[{ t: "Time" }, { t: "Product" }, { t: "Customer" }, { t: "Qty", r: 1 }, { t: "Total", r: 1 }, { t: "Type", r: 1 }]} />
            <tbody>
              {todaySales.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: "34px 16px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                    No sales recorded today
                  </td>
                </tr>
              )}
              {todaySales.slice(0, 6).map((s, i) => {
                const isCredit = !!s.customer_name;
                return (
                  <tr key={i}
                    style={{ cursor: "pointer" }}
                    onClick={() => go("products")}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <TD mut>{fmtTime(s.date || s.created_at)}</TD>
                    <TD b>{s.product_name}</TD>
                    <TD mut>{s.customer_name || "Walk-in"}</TD>
                    <TD r>{Math.abs(s.quantity)}</TD>
                    <TD r b>{MMK(s.total_price)} Ks</TD>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                      <Pill tone={isCredit ? "credit" : "cash"}>{isCredit ? "Credit" : "Cash"}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {/* Low Stock */}
        <Card>
          <div style={{
            padding: "16px 20px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: "1px solid var(--hair)",
          }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>
              Low Stock
            </div>
            <Btn kind="soft" sm icon="restock" onClick={() => go("restock")}>Restock</Btn>
          </div>

          <div style={{ padding: "6px 8px" }}>
            {lowStock.length === 0 ? (
              <div style={{ padding: "28px 12px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                All products well stocked
              </div>
            ) : (
              lowStock.slice(0, 6).map(p => {
                const st = stockStatus(p);
                return (
                  <div key={p.id}
                    onClick={() => go("products")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--sub)", marginTop: 2 }}>
                        {p.quantity} left · reorder {p.low_stock_threshold}
                      </div>
                    </div>
                    <Pill tone={st}>{st === "out" ? "Out" : "Low"}</Pill>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
